import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AppleLogo,
  Car,
  Check,
  ForkKnife,
  GoogleLogo,
  GraduationCap,
  HandHeart,
  Heart,
  Info,
  MapPin,
  Minus,
  Money,
  PawPrint,
  Plus,
  Question,
  Scales,
  Smiley,
  User,
  UsersThree,
  Wine,
  Wrench,
} from '@phosphor-icons/react';
import { MotionReveal, itemVariants, replayingViewport } from '@components/motion/scroll-motion';
import { ROUTES } from '@routes/route-paths';
import { CarouselDots, useCarousel } from './carousel';
import { colors } from '@config/colors';
import { heightMedia, media } from '@config/breakpoints';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';

/*
 * Create an account: the app's own three onboarding steps, drawn as the
 * app draws them (June, 2026-09-23, from its screens) — the household with
 * its zip, the goals, the account — each under the app's progress pills,
 * with one line on what giving it buys. The steps, their pieces and their
 * goals are home-ceo-mobile's: `(onboarding)/household.tsx`, `goals.tsx`,
 * `(auth)/sign-up.tsx`, `config/goals-catalogue.ts`; the first two lines
 * are from its "Why we ask".
 */

/** The household step's counters, as the app draws them. */
const MEMBERS = [
  { Icon: User, label: 'Adults', count: 2 },
  { Icon: Smiley, label: 'Children', count: 2 },
  { Icon: PawPrint, label: 'Pets', count: 1 },
  { Icon: Car, label: 'Cars', count: 2 },
] as const;

/** The row over the counters: one icon for each of them. */
const SUMMARY = [
  { Icon: User, key: 'adult-1' },
  { Icon: User, key: 'adult-2' },
  { Icon: Smiley, key: 'child-1' },
  { Icon: Smiley, key: 'child-2' },
  { Icon: Car, key: 'car-1' },
  { Icon: Car, key: 'car-2' },
  { Icon: PawPrint, key: 'pet-1' },
] as const;

/** The goals step's choices, in the app's order, two of them picked. */
const GOALS = [
  { Icon: UsersThree, label: 'Quality Time', picked: true },
  { Icon: HandHeart, label: 'Health', picked: false },
  { Icon: Car, label: 'Car', picked: false },
  { Icon: Money, label: 'Finances', picked: false },
  { Icon: Wine, label: 'Date Nights', picked: true },
  { Icon: ForkKnife, label: 'Groceries', picked: false },
  { Icon: Wrench, label: 'House', picked: false },
  { Icon: GraduationCap, label: 'Kid’s Education', picked: false },
  { Icon: Scales, label: 'Career', picked: false },
  { Icon: PawPrint, label: 'Pet Care', picked: false },
  { Icon: Heart, label: 'Growing a Family', picked: false },
  { Icon: Question, label: 'Other', picked: false },
] as const;

const chipVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
} as const;

const STEP_NAMES = ['Your household', 'Your goals', 'Your account'] as const;

/** How long a phone or tablet shows each step while the page plays. */
const STEP_MS = 3600;

/* A short phone (an iPhone SE) gets the section onto one screen: the
   lead steps back and the app's card tightens. */
const SHORT_PHONE = heightMedia.shortPhone;

const cascade = (stagger: number, delay: number) => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren: delay } },
});

/** The app's progress pills over each step: done, this one, to come. */
const Progress: React.FC<{ step: 0 | 1 | 2 }> = ({ step }) => (
  <Pills aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <PillBar key={i} $state={i < step ? 'done' : i === step ? 'here' : 'next'} />
    ))}
  </Pills>
);

type GettingStartedProps = {
  /** The page's autoplay is on this section: a phone steps through the three. */
  playing?: boolean;
  /** Take the reader to where the app is to be had: the account step
   *  leads there from anywhere but its Terms and Privacy links. */
  onGetApp?: () => void;
};

/** Following a legal link, not the card around it. */
const followLink = (e: React.MouseEvent) => e.stopPropagation();

export const GettingStarted: React.FC<GettingStartedProps> = ({ playing = false, onGetApp }) => {
  const { trackRef, current, goTo } = useCarousel<HTMLOListElement>(STEP_NAMES.length, playing, STEP_MS, {
    loop: false,
  });
  return (
    <Root>
      <Header>
        <MotionReveal>
          <Eyebrow>Getting started</Eyebrow>
          <Heading>Create an Account Today</Heading>
        </MotionReveal>
        <MotionReveal delay={0.15}>
          <Lead>Three quick steps in the app, and every answer after is made for your household.</Lead>
        </MotionReveal>
      </Header>

      <Steps
        ref={trackRef}
        as={motion.ol}
        initial="hidden"
        whileInView="show"
        viewport={replayingViewport}
        variants={cascade(0.14, 0.15)}
      >
        <Step as={motion.li} variants={itemVariants}>
          <Progress step={0} />
          <Screen>
            <Title>
              Your Household
              <InfoMark aria-hidden="true">
                <Info weight="bold" />
              </InfoMark>
            </Title>
            <Picture as={motion.div} variants={cascade(0.07, 0.35)} aria-hidden="true">
              <Summary as={motion.div} variants={chipVariants}>
                {SUMMARY.map(({ Icon, key }) => (
                  <Icon key={key} />
                ))}
              </Summary>
              <Counters>
                {MEMBERS.map(({ Icon, label, count }) => (
                  <Counter as={motion.div} key={label} variants={chipVariants}>
                    <CounterIcon>
                      <Icon />
                    </CounterIcon>
                    <CounterLabel>{label}</CounterLabel>
                    <Stepper>
                      <Round>
                        <Minus weight="bold" />
                      </Round>
                      <Count>{count}</Count>
                      <Round>
                        <Plus weight="bold" />
                      </Round>
                    </Stepper>
                  </Counter>
                ))}
              </Counters>
              <Zip as={motion.div} variants={chipVariants}>
                <CounterIcon>
                  <MapPin />
                </CounterIcon>
                <CounterLabel>Zip Code</CounterLabel>
                <ZipField>94303</ZipField>
              </Zip>
            </Picture>
          </Screen>
          <Payoff>Plans that fit everyone, with help nearby.</Payoff>
        </Step>

        <Step as={motion.li} variants={itemVariants}>
          <Progress step={1} />
          <Screen>
            <Title>
              Your Goals
              <InfoMark aria-hidden="true">
                <Info weight="bold" />
              </InfoMark>
            </Title>
            <Picture as={motion.div} variants={cascade(0.04, 0.35)} aria-hidden="true">
              <Goals>
                {GOALS.map(({ Icon, label, picked }) => (
                  <Goal as={motion.div} key={label} variants={chipVariants} $picked={picked}>
                    <GoalIcon $picked={picked}>
                      <Icon />
                    </GoalIcon>
                    <GoalLabel>{label}</GoalLabel>
                  </Goal>
                ))}
              </Goals>
            </Picture>
          </Screen>
          <Payoff>Answers aimed at what matters most to you.</Payoff>
        </Step>

        <Step as={motion.li} variants={itemVariants}>
          <Progress step={2} />
          {/* The account is made in the app: a click anywhere on this card
              but its legal links fast-forwards to the Download section
              (June, 2026-09-23). Its buttons are real, for the keyboard. */}
          <Screen $leads onClick={onGetApp}>
            <Title>Create Your Account</Title>
            <Picture as={motion.div} variants={cascade(0.12, 0.35)}>
              <Agree as={motion.div} variants={chipVariants}>
                <Box aria-hidden="true">
                  <Check weight="bold" />
                </Box>
                <span>
                  By continuing you agree to the{' '}
                  <Legal to={ROUTES.LEGAL_INDEX} onClick={followLink}>
                    Terms
                  </Legal>{' '}
                  and{' '}
                  <Legal to={ROUTES.LEGAL_INDEX} onClick={followLink}>
                    Privacy Policy
                  </Legal>
                </span>
              </Agree>
              <Social as={motion.button} type="button" variants={chipVariants}>
                <GoogleLogo weight="bold" aria-hidden="true" />
                Continue with Google
              </Social>
              <Social as={motion.button} type="button" variants={chipVariants} $dark>
                <AppleLogo weight="fill" aria-hidden="true" />
                Continue with Apple
              </Social>
              <SignIn as={motion.div} variants={chipVariants}>
                Already have an account? <SignInButton type="button">Sign in</SignInButton>
              </SignIn>
            </Picture>
          </Screen>
          <Payoff>Saved once, so every wish starts ahead.</Payoff>
        </Step>
      </Steps>
      <CarouselDots labels={STEP_NAMES} current={current} onSelect={goTo} label="Getting started" hideFrom="lg" />
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${spacing.xl};

  ${SHORT_PHONE} {
    margin-bottom: ${spacing.md};
  }

  ${media.lg} {
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    gap: ${spacing['5xl']};
    align-items: end;
    margin-bottom: ${spacing['2xl']};
  }
`;

const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};
`;

const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.6vw, 3.75rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.md};

  ${SHORT_PHONE} {
    font-size: 1.75rem;
    margin: 0;
  }
`;

const Lead = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(0.9375rem, 1.3vw, 1.125rem);
  line-height: 1.6;
  color: ${colors.highlight.primary};
  margin: 0;

  ${media.lg} {
    margin-bottom: ${spacing.md};
  }

  ${SHORT_PHONE} {
    display: none;
  }
`;

/* Phones and tablets swipe between the three; a wide screen stands them
   side by side. */
const Steps = styled.ol`
  list-style: none;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 86%;
  column-gap: ${spacing.md};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  margin: 0 -${spacing.lg};
  padding: ${spacing.xs} ${spacing.lg} ${spacing.md};
  scroll-padding-inline: ${spacing.lg};

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.md} {
    grid-auto-columns: 58%;
    column-gap: ${spacing.lg};
  }

  /* minmax(0, 1fr), not 1fr: a step's pieces would otherwise hold its
     column wider than the other two. */
  ${media.lg} {
    grid-auto-flow: row;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing.xl};
    overflow: visible;
    margin: 0;
    padding: 0;
  }
`;

/* A step: the app's pills, its card as the app draws it, and one line. */
const Step = styled.li`
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.md};

  ${SHORT_PHONE} {
    gap: ${spacing.sm};
  }
`;

const Pills = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing.sm};
`;

const PillBar = styled.span<{ $state: 'done' | 'here' | 'next' }>`
  width: 44px;
  height: 6px;
  border-radius: ${borderRadius.full};
  background: ${({ $state }) =>
    $state === 'here' ? colors.accent.coral.extraDark : $state === 'done' ? colors.accent.coral.dark : colors.highlight.light};
`;

/* The app's glass card; the account's leads on to the app. */
const Screen = styled.div<{ $leads?: boolean }>`
  cursor: ${({ $leads }) => ($leads ? 'pointer' : 'default')};
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  padding: ${spacing.lg} ${spacing.md} ${spacing.md};
  border-radius: ${borderRadius['3xl']};
  background: ${colors.overlayWhite70};
  border: 1px solid ${colors.overlayWhite90};
  box-shadow: 0 20px 50px ${colors.shadowSoft};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  ${SHORT_PHONE} {
    gap: ${spacing.sm};
    padding: ${spacing.md} ${spacing.sm} ${spacing.sm};
  }
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.3;
  color: ${colors.neutral.extraDark};
  margin: 0;
`;

const InfoMark = styled.span`
  display: inline-grid;
  color: ${colors.accent.coral.exxtraDark};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Picture = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const glassTile = css`
  border-radius: ${borderRadius['2xl']};
  background: ${colors.overlayWhite80};
  border: 1px solid ${colors.overlayWhite90};
  box-shadow: 0 4px 12px ${colors.shadowSoft};
`;

const Summary = styled.div`
  ${glassTile};
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: ${spacing.md};
  color: ${colors.accent.coral.exxtraDark};

  svg {
    width: 20px;
    height: 20px;
  }

  ${SHORT_PHONE} {
    padding: ${spacing.sm};

    svg {
      width: 17px;
      height: 17px;
    }
  }
`;

const Counters = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing.sm};
`;

const Counter = styled.div`
  ${glassTile};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: ${spacing.sm} ${spacing.xs};

  ${SHORT_PHONE} {
    gap: 2px;
    padding: 6px ${spacing.xs};
  }
`;

const CounterIcon = styled.span`
  display: inline-grid;
  color: ${colors.brand.exxtraDark};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const CounterLabel = styled.span`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${colors.neutral.extraDark};
`;

const Stepper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const Round = styled.span`
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;

  ${SHORT_PHONE} {
    width: 22px;
    height: 22px;
  }

  border-radius: 50%;
  background: ${colors.brand.primary};
  color: ${colors.highlight.primary};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Count = styled.span`
  min-width: 1em;
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 1rem;
  color: ${colors.neutral.extraDark};
`;

const Zip = styled.div`
  ${glassTile};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};

  ${CounterLabel} {
    flex: 1;
  }
`;

const ZipField = styled.span`
  padding: 4px ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.neutral.light};
  background: ${colors.white};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.9375rem;
  color: ${colors.neutral.extraDark};
`;

const Goals = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
`;

/* A goal, as the app draws it; picked, it turns slate with its icon on
   coral (white on the slate reads 4.8:1). */
const Goal = styled.div<{ $picked: boolean }>`
  ${glassTile};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 72px;
  padding: 6px 2px;

  ${({ $picked }) =>
    $picked &&
    css`
      background: ${colors.highlight.light};
      border-color: ${colors.highlight.light};
      color: ${colors.white};
    `}
`;

const GoalIcon = styled.span<{ $picked: boolean }>`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ $picked }) =>
    $picked ? `linear-gradient(135deg, ${colors.accent.coral.dark}, ${colors.accent.coral.extraDark})` : colors.white};
  color: ${({ $picked }) => ($picked ? colors.white : colors.neutral.extraDark)};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const GoalLabel = styled.span`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.6875rem;
  line-height: 1.2;
  text-align: center;
`;

const Agree = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  padding: ${spacing.xs} 0;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${colors.neutral.darkMedium};
`;

const Legal = styled(Link)`
  font-weight: 500;
  color: ${colors.neutral.extraDark};
  text-decoration: underline;

  &:hover {
    color: ${colors.brand.exxxtraDark};
  }
`;

const Box = styled.span`
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: ${borderRadius.sm};
  background: linear-gradient(135deg, ${colors.accent.coral.dark}, ${colors.accent.coral.extraDark});
  color: ${colors.white};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Social = styled.div<{ $dark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  width: 100%;
  cursor: pointer;
  padding: ${spacing.md};
  border-radius: ${borderRadius.full};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.9375rem;
  font-weight: 500;
  background: ${({ $dark }) => ($dark ? colors.neutral.extraDark : colors.white)};
  color: ${({ $dark }) => ($dark ? colors.white : colors.neutral.extraDark)};
  border: 1px solid ${({ $dark }) => ($dark ? colors.neutral.extraDark : colors.neutral.light)};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SignIn = styled.div`
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.8125rem;
  color: ${colors.neutral.medium};
`;

const SignInButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 500;
  color: ${colors.accent.coral.exxxtraDark};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* What giving it buys — one line, centred, a size down on a phone so it
   stays one. */
const Payoff = styled.p`
  margin: 0;
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${colors.brand.exxxtraDark};

  ${media.sm} {
    font-size: 0.9375rem;
  }
`;
